import { queryOptions } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { MonthEndSummary } from './types';

/**
 * Transport for Month end.
 *
 * ── ASSUMED CONTRACT ──────────────────────────────────────────────────────────
 *   GET /workspaces/:workspaceId/month-end?month=YYYY-MM  -> MonthEndSummary
 *
 * Gated `billing.read` server-side (BillingController). A caller without it
 * never reaches this screen — the nav entry in AppShell.tsx hides it and the
 * route guard in routes.tsx blocks direct navigation — but the API is the
 * actual authority regardless of what the client asserts, same note as every
 * other feature's api.ts in this codebase.
 *
 * Query key is local to this feature, per the file-ownership split for this
 * milestone: `['ws', workspaceId, 'month-end', month]`, matching the
 * `['ws', id, …]` prefix rule in lib/queryKeys.ts without editing that shared
 * file. The lead folds this into queryKeys.ts when the module is wired into
 * app.module.ts / routes.tsx.
 * ────────────────────────────────────────────────────────────────────────────
 */

export const monthEndKeys = {
  summary: (workspaceId: string, month: string) => ['ws', workspaceId, 'month-end', month] as const,
} as const;

function path(workspaceId: string, month: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/month-end?month=${encodeURIComponent(month)}`;
}

export function fetchMonthEnd(
  workspaceId: string,
  month: string,
  signal?: AbortSignal,
): Promise<MonthEndSummary> {
  return api.get<MonthEndSummary>(path(workspaceId, month), signal);
}

export function monthEndQueryOptions(workspaceId: string, month: string) {
  return queryOptions({
    queryKey: monthEndKeys.summary(workspaceId, month),
    queryFn: ({ signal }) => fetchMonthEnd(workspaceId, month, signal),
  });
}
